import { fetchWrapper } from '@/utils.js'
const MESSAGES = window.MESSAGES_ENDPOINT

export class MessagesController {
  constructor (organizationId, token) {
    this.token = token
    this.options = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Token ${token}`,
        'X-Organization-Id': organizationId
      }
    }
    this.organizationId = organizationId
    this._fetch = fetch
  }

  fetch (url, options, useWrapper = true) {
    const promise = this._fetch(url, options)
    if (useWrapper) {
      return fetchWrapper(promise)
    } else {
      return promise
    }
  }

  getOrRequestTicketAPITranslation (message, forceInit) {
    const payload = {
      thread__code: message.threadCode,
      message__code: message.messageCode,
      author_code: message.authorCode,
      author_role: message.authorRole,
      agent_email: message.agentEmail,
      subject: message.subject,
      content: message.content,
      content_html: message.contentHtml,
      is_private: false
    }
    return this.fetch(
      `${MESSAGES}/api/freshworks/v1/onConversationCreate/?requires_existing_ticket=${!forceInit}&applet_call=true`,
      {
        ...this.options,
        method: 'POST',
        body: JSON.stringify(payload)
      },
      false
    )
      .then((response) => {
        try {
          const ticketStatus = response.json()
          return ticketStatus
        } catch (err) {
          return null
        }
      })
  }

  changeCustomerLanguage (
    organizationId,
    ticketCode,
    newCustomerLanguage,
    agentCode,
    agentLanguageId,
    agentLanguages
  ) {
    const payload = {
      'customer_language_id': newCustomerLanguage,
      'agent_external_id': agentCode,
      'agent_language_id': agentLanguageId,
      'skip_customer_languages': agentLanguages
    }
    return this.fetch(
      `${MESSAGES}/api/freshworks/v1/changeCustomerLanguage/${organizationId}/${ticketCode}/`,
      {
        ...this.options,
        method: 'POST',
        body: JSON.stringify(payload)
      }
    )
  }

  upgradeLastMachineTranslation (organizationId, ticketCode) {
    return this.fetch(
      `${MESSAGES}/api/freshworks/v1/upgradeTranslation/${organizationId}/${ticketCode}/`,
      {
        ...this.options,
        method: 'POST',
        body: JSON.stringify({})
      }
    )
  }

  requestTranslation (messageId, language, mode = 'mt') {
    return this.fetch(`${MESSAGES}/api/core/v1/messages/${messageId}/requestTranslation/`, {
      ...this.options,
      method: 'POST',
      body: JSON.stringify({
        language,
        translation_mode: mode
      })
    })
  }

  detectLanguage (messageId) {
    return this.fetch(`${MESSAGES}/api/core/v1/messages/${messageId}/detectLanguage/`, {
      ...this.options,
      method: 'PUT'
    })
  }

  updateAgent (agentId, payload) {
    return this.fetch(`${MESSAGES}/api/core/v1/agents/${agentId}/`, {
      ...this.options,
      method: 'PATCH',
      body: JSON.stringify(payload)
    })
  }

  createAgent (agentCode, agentEmail, authorized = true, preferredLanguage = 'en') {
    return this.fetch(`${MESSAGES}/api/core/v1/agents/`, {
      ...this.options,
      method: 'POST',
      body: JSON.stringify({
        code: agentCode,
        email: agentEmail,
        authorized,
        organization: this.organizationId,
        preferred_language: preferredLanguage
      })
    })
  }

  fetchLastTranslationForThread (threadId) {
    const url = `${MESSAGES}/api/core/v1/translations/?thread=${threadId}&organization=${this.organizationId}&order=-created&limit=1&include_message=true`
    return this.fetch(url, this.options)
      .then(({ results }) => {
        return results.length ? results[0] : null
      })
  }

  async fetchThreadAndFirstMessage (payload) {
    const result = await this.threadForMessage(payload.message, false)
    return result
  }

  async threadForFirstMessage (payload) {
    let result = await this.threadForMessage(payload.message, true)
    return result
  }

  async threadHasTranslationsForLP (threadId, sourceCode, targetCode, authorRole = 'customer') {
    const url = `${MESSAGES}/api/core/v1/translations/?thread=${threadId}&source__code=${sourceCode}&target__code=${targetCode}&author__role=${authorRole}`
    return this.fetch(url, this.options)
      .then(({ results: { length } }) => {
        return length > 0
      })
  }

  fetchAgent (agentCode, organizationCode = null) {
    let organizationFilter = organizationCode
      ? `organization_code=${organizationCode}` : this.organizationId
        ? `organization_id=${this.organizationId}` : ''
    const url = `${MESSAGES}/api/freshdesk/v1/agents/${agentCode}/?${organizationFilter}`
    return this.fetch(url, this.options)
      .then((agent) => agent)
      .catch((err) => {
        if (err.status === 404) {
          return null
        }
        const error = new Error(err.response)
        error.status = err.status
        throw error
      })
  }

  fetchSettings () {
    return this.fetch(
      `${MESSAGES}/api/frontdesk/v1/organizationSettings/?organization=${this.organizationId}`,
      this.options).then(({ results }) => results.length ? results[0] : null)
  }

  threadForMessage (email, canCreate = true) {
    const query = canCreate ? '?can_create=true' : '?can_create=false'
    const url = `${MESSAGES}/api/core/v1/thread/forMessage${query}`
    const { subject, content, language } = email
    const payload = {
      organization: this.organizationId,
      thread__code: email.threadCode,
      message__code: email.messageCode,
      language,
      subject,
      content,
      content_html: email.contentHtml,
      author_code: email.authorCode,
      author_role: email.authorRole,
      thread__data: email.threadData,
      message__data: email.messageData
    }

    return this.fetch(url, { ...this.options, method: 'PUT', body: JSON.stringify(payload) })
  }

  async fetchToken (username, password, organizationCode) {
    const options = {
      body: JSON.stringify({ username, password }),
      method: 'post',
      headers: {
        'content-type': 'application/json'
      }
    }
    const filter = organizationCode
      ? `?organization__code=${organizationCode}` : ''

    return this.fetch(`${MESSAGES}/api/core/v1/apiTokenAuth/${filter}`, options)
      .then(({ token }) => token)
  }

  async fetchOrganizationId (organizationCode, system, token) {
    const options = {
      method: 'get',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Token ${token}`
      }
    }

    return this.fetch(`${MESSAGES}/api/core/v1/organizations/?code=${organizationCode}&system__code=${system}`, options)
      .then(({ results }) => {
        if (results.length === 1) {
          return results[0].id
        } else if (results.length === 0) {
          console.error(`Couldn't find organization with the token provided`)
        } else {
          console.error(`Multiple organization found!`)
        }
      })
  }

  // used by action "signInWithApiKeyAction"
  async signInWithApiKey (apiKey, organizationCode, systemCode) {
    const token = await this.fetchToken('apiKeyLogin', apiKey, organizationCode)
    const organizationId = await this.fetchOrganizationId(organizationCode, systemCode, token)
    return { token, organizationId }
  }

  static create (organizationId, token) {
    if (organizationId && token) {
      return new MessagesController(organizationId, token)
    } else {
      return null
    }
  }
}



// WEBPACK FOOTER //
// ./src/api/messages.js